/* eslint-disable @typescript-eslint/no-empty-function */
import { format } from 'date-fns';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import { Container } from './styles';

interface IResultHeader {
  setHeaderWidth?: Dispatch<SetStateAction<number | null>>;
  lastRoutingDate: Date | null;
  consultStatus?: string;
  lastImplantationDate?: string | Date | null;
  isImplant?: boolean;
  sendForRouting: () => Promise<void>;
  handleOpenAdjustmentModal: () => void;
  setManualRoutingModalShow: Dispatch<SetStateAction<boolean>>;
  setImplantationModalShow: Dispatch<SetStateAction<boolean>>;
  setMyLinkModalShow: Dispatch<SetStateAction<boolean>>;
  routingDocumentDownloadLink: string;
  canEnableActions: boolean;
  isConsultValid: boolean;
  setMapDataViewModalShow: Dispatch<SetStateAction<boolean>>;
  downloadMrRoutingDocument: () => Promise<void>;
  isMultiRoute: boolean;
  implantationStatus: string;
  lastReclamation: string;
  showManualRoutingButton: boolean;
}

export default function ResultHeader({
  setHeaderWidth,
  lastRoutingDate,
  consultStatus,
  lastImplantationDate,
  isImplant,
  sendForRouting,
  handleOpenAdjustmentModal,
  setManualRoutingModalShow,
  routingDocumentDownloadLink,
  setImplantationModalShow,
  setMyLinkModalShow,
  canEnableActions,
  isConsultValid,
  setMapDataViewModalShow,
  downloadMrRoutingDocument,
  isMultiRoute,
  implantationStatus,
  lastReclamation,
  showManualRoutingButton,
}: IResultHeader) {
  const resultHeaderRef = useRef<HTMLDivElement>(null);
  const downloadDocumentElementRef = useRef<HTMLAnchorElement>(null);

  const formattedRoutingDate = useMemo(() => {
    if (!lastRoutingDate) return '';
    try {
      const date = new Date(lastRoutingDate);
      if (isNaN(date.getTime())) return '';
      return format(date, 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\'');
    } catch {
      return '';
    }
  }, [lastRoutingDate]);

  const formattedImplantationDate = useMemo(() => {
    if (!lastImplantationDate) return '';
    try {
      const date = new Date(lastImplantationDate);
      if (isNaN(date.getTime())) return '';
      return format(date, 'dd/MM/yyyy \'às\' HH\'h\'mm\'m\'');
    } catch {
      return '';
    }
  }, [lastImplantationDate]);

  const hasImplanted = isImplant ?? (implantationStatus.startsWith('implant') || consultStatus === 'Implantado');

  useEffect(() => {
    // Verifique se a referência do elemento existe
    if (resultHeaderRef.current && setHeaderWidth) {
      // Acesse a propriedade offsetWidth para obter o width do elemento
      const width = resultHeaderRef.current.offsetWidth;
      // Atualize o state com o width obtido
      setHeaderWidth(width);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultHeaderRef.current, setHeaderWidth]);

  function downloadDocument() {
    downloadDocumentElementRef.current?.click();
  }

  const { isCustomer, permissions } = useGetGeneralAppData();
  const routingPermissions = permissions.find((x) => x.codPag === 6);
  const reclamationsPermissions = permissions.find((x) => x.codPag === 8);

  return (
    <Container ref={resultHeaderRef}>
      <div>
        <strong>
          Resultado
        </strong>
        <div className="dates">
          {formattedRoutingDate && (
            <small>
              Roteirizado em {formattedRoutingDate}
            </small>
          )}
          {hasImplanted && formattedImplantationDate && (
            <small className="green">
              Implantado em {formattedImplantationDate}
            </small>
          )}
        </div>
      </div>
      <div className="actionButtons">
        <button type="button" onClick={() => sendForRouting()} disabled={!canEnableActions || implantationStatus.startsWith('implant')}>Roteirizar</button>
        {!isCustomer && (
          <button type="button"
            onClick={() => {
              setManualRoutingModalShow(true);
              if (!lastReclamation) return;
              toast.info('Última reclamação: ' + lastReclamation, { toastId: 'lastReclamationDetails', autoClose: false });
            }}
            disabled={!canEnableActions || !isConsultValid || implantationStatus.startsWith('implant')}>Rot Manual</button>
        )}
        {isCustomer && showManualRoutingButton && (
          <button type="button" onClick={() => setManualRoutingModalShow(true)}>Informar bilhetes manualmente</button>
        )}
        {reclamationsPermissions?.vizualizar && (
          <button type="button" onClick={() => handleOpenAdjustmentModal()}>Contestações</button>
        )}
        {routingPermissions?.alterar && (
          <button type="button" onClick={() => setImplantationModalShow(true)} disabled={!canEnableActions}>Implantação</button>
        )}
        {routingPermissions?.imprimir && (
          <button type="button" onClick={() => isMultiRoute ? downloadMrRoutingDocument() : downloadDocument()} disabled={(!routingDocumentDownloadLink && !isMultiRoute) || !canEnableActions}>⬇️ Carta</button>
        )}
        <button type="button" onClick={() => setMyLinkModalShow(true)} disabled={!canEnableActions}>📩 My-Link</button>
        <button type="button" onClick={() => setMapDataViewModalShow(true)}>🗺️ Tendência</button>
      </div>

      <a href={routingDocumentDownloadLink} download target="_blank" rel="noreferrer" style={{ display: 'none' }} ref={downloadDocumentElementRef} />
    </Container>
  );
}
